/**
 * Client for GET /{tenant}/global-filter/facets (tech-spec.md §11).
 *
 * Replaces GlobalFilterContext.refreshFacets's previous listCloudAccounts-
 * based derivation: the backend now computes the tenant's distinct
 * accounts + environments in one round-trip instead of the client paging
 * up to 1000 accounts and deriving distinct values in JS.
 */

import { apiFetch } from './api/core'

export type GlobalFilterFacetAccount = {
  id: string
  label: string
  environment: string | null
  // hasResources is false only when this account has zero rows across every
  // account-scoped resource domain tenant-wide (PENG-13403 PM follow-up —
  // see facets-accounts.sql on the backend for the exact table list). The
  // backend sorts `accounts` by resource count descending; the raw count
  // itself is never sent — only this flag, so the UI can show an explicit
  // "no resources available" label without displaying a number for every
  // other account.
  hasResources: boolean
}

// Response mapper: {accounts, environments} from the backend maps to
// {accountIds, accounts, environments}. GlobalFilterContext's own facets
// state still only tracks accountIds/environments (tech-spec.md §8
// Signature and Purpose) — but per tech-spec.md §11, "account.label is used
// by the dropdown component if surfaced", so the raw fetch result carries
// the full per-account {id,label,environment} records too, for a dropdown
// (or other caller) that wants labels. `accounts` is omitted (not an empty
// array) on the 404-fallback path below, since there's no account data to
// surface in that case.
export type GlobalFilterFacets = {
  accountIds: string[]
  accounts?: GlobalFilterFacetAccount[]
  environments: string[]
}

// Wire shape: has_resources (snake_case) is mapped to hasResources below,
// matching the backend's JSON field name (see globalfilterfacets.Account).
type WireFacetAccount = {
  id: string
  label: string
  environment: string | null
  has_resources: boolean
}

type FacetsResponse = {
  accounts: WireFacetAccount[]
  environments: string[]
}

// @purpose Fetch the tenant's account/environment facets for the global filter dropdown from the dedicated facets endpoint; treats a missing endpoint (404) as empty facets rather than an error
export async function fetchGlobalFilterFacets(
  tenantId: string,
  signal?: AbortSignal,
): Promise<GlobalFilterFacets> {
  try {
    const data = await apiFetch<FacetsResponse>(
      '/global-filter/facets',
      { signal },
      tenantId,
    )
    // Preserve the backend's resource-count-descending order — do not sort
    // client-side.
    const accounts: GlobalFilterFacetAccount[] = (data.accounts ?? []).map(
      acc => ({
        id: acc.id,
        label: acc.label,
        environment: acc.environment,
        hasResources: acc.has_resources,
      }),
    )
    return {
      accountIds: accounts.map(acc => acc.id),
      accounts,
      environments: data.environments ?? [],
    }
  } catch (err) {
    if (
      err &&
      typeof err === 'object' &&
      'status' in err &&
      err.status === 404
    ) {
      return { accountIds: [], environments: [] }
    }
    throw err
  }
}
